import { Box, Grid, Modal } from "@mui/material";
import { capitalize } from "lodash";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { OverflowText } from "components";
import { formatNumber } from "utils/formatNumber";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useMemo } from "react";
import { formatDate } from "utils/formatDate";

const DetailTransactionModal = ({ modalProps = {}, data, onClose }) => {
  const sharesOwnershipPercentage = useMemo(() => {
    const prevAmt = data?.company_total_shares?.prev_total_shares
      ? (
          (Number(data?.prev_shares) /
            Number(data?.company_total_shares?.prev_total_shares)) *
          100
        )?.toFixed(2)
      : 0;
    const currAmt = data?.company_total_shares?.current_total_shares
      ? (
          (Number(data?.current_shares) /
            Number(data?.company_total_shares?.current_total_shares)) *
          100
        )?.toFixed(2)
      : 0;
    const formattedPrevAmt = `${prevAmt || "0.00"}% ownership`;
    const formattedCurrAmt = `${currAmt || "0.00"}% ownership`;

    return {
      prevAmt,
      currAmt,
      formattedPrevAmt,
      formattedCurrAmt,
    };
  }, [data]);

  const gainLossOwnershipPercentage = useMemo(() => {
    const isGain = (data?.prev_shares || 0) <= (data?.current_shares || 0);
    const gainLossAmt = Math.abs(
      (Number(sharesOwnershipPercentage?.prevAmt) || 0) -
        (Number(sharesOwnershipPercentage?.currAmt) || 0),
    )?.toFixed(2);

    return (
      <div
        className={`rounded-full px-2 py-1 text-xs max-w-[100px] max-h-[30px] ${
          isGain ? "bg-[#E6F5EF] text-[#039E5A]" : "bg-[#FCEAED] text-[#E52A49]"
        }`}
      >
        <div className='flex flex-row gap-2 align-middle'>
          {isGain ? (
            <TrendingUpOutlinedIcon className='text-xs' />
          ) : (
            <TrendingDownIcon className='text-xs' />
          )}
          <p className='text-xs mt-1'>{`${
            isGain ? "+" : "-"
          }${gainLossAmt}%`}</p>
        </div>
      </div>
    );
  }, [data, sharesOwnershipPercentage]);

  return (
    <Modal {...modalProps}>
      <Box
        sx={{
          position: "absolute",
          top: "55%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          overflow: "scroll",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          px: "20px",
          py: "25px",
        }}
      >
        <div className='detail-modal-content'>
          <div className='flex flex-row gap-2 pb-6 border-b border-[#EFF1F2]'>
            <CloseOutlinedIcon className='cursor-pointer' onClick={onClose} />
            <p className='font-semibold'>Transaction Detail</p>
          </div>
          <div className='grid gap-4 mt-6 mb-6'>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <div>
                  <p className='text-sm text-[#5F737F]'>Date</p>
                  <p className='text-sm'>
                    {formatDate.toShortDate(data?.rawDate)}
                  </p>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div>
                  <p className='text-sm text-[#5F737F]'>SID</p>
                  <p className='text-sm'>{data?.rawSid}</p>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div>
                  <p className='text-sm text-[#5F737F]'>Name</p>
                  <OverflowText text={data?.rawName} textClassName='text-sm' />
                </div>
              </Grid>
              <Grid item xs={6}>
                <div>
                  <p className='text-sm text-[#5F737F] mb-1'>Type</p>
                  {data?.rawTransactionType === "BUY" ||
                  data?.rawTransactionType === "SELL" ? (
                    <div
                      className={`rounded-full px-2 py-1 text-sm max-w-[42px] ${
                        data?.rawTransactionType === "BUY"
                          ? "bg-[#E6F5EF] text-[#039E5A]"
                          : "bg-[#FCEAED] text-[#E52A49]"
                      }`}
                    >
                      {capitalize(data?.rawTransactionType)}
                    </div>
                  ) : (
                    <p className='text-sm'>
                      {capitalize(data?.rawTransactionType || "None")}
                    </p>
                  )}
                </div>
              </Grid>
              <Grid item xs={6}>
                <div>
                  <p className='text-sm text-[#5F737F]'>Shares Traded</p>
                  <p className='text-sm'>
                    {formatNumber.toAmount(data?.shares_amount)}
                  </p>
                </div>
              </Grid>
            </Grid>
          </div>

          <div>
            <p className='text-sm text-[#5F737F] mb-4'>Share Change</p>
            <div className='flex flex-row justify-between align-middle'>
              <div className='flex flex-row gap-4'>
                <div className='flex flex-col gap-2'>
                  <p className='text-sm'>
                    {formatNumber.toAmount(data?.prev_shares)}
                  </p>
                  <p className='text-xs'>
                    {sharesOwnershipPercentage?.formattedPrevAmt}
                  </p>
                </div>
                <img
                  alt='right-arrow-icon'
                  src='/images/right-arrow-circled.svg'
                />
                <div className='flex flex-col gap-2'>
                  <p className='text-sm'>
                    {formatNumber.toAmount(data?.current_shares)}
                  </p>
                  <p className='text-xs'>
                    {sharesOwnershipPercentage?.formattedCurrAmt}
                  </p>
                </div>
              </div>

              {gainLossOwnershipPercentage}
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default DetailTransactionModal;
